import { format } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import {
  ANALYTICS_EVENT_TYPES,
  type AnalyticsEventType
} from '@services/event-analytics/constants'
import type { EventAnalyticsEventsList } from '@services/event-analytics/types'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

const ALL_EVENTS_VALUE = '__all__'

interface EventAnalyticsEventsTableProps {
  data?: EventAnalyticsEventsList
  loading?: boolean
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  eventFilter: AnalyticsEventType | null
  onEventFilterChange: (event: AnalyticsEventType | null) => void
}

export function EventAnalyticsEventsTable({
  data,
  loading = false,
  page,
  pageSize,
  onPageChange,
  eventFilter,
  onEventFilterChange
}: EventAnalyticsEventsTableProps) {
  const { t } = useTranslation('eventAnalytics')

  const TH =
    'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground'
  const TD = 'px-4 py-3 align-middle text-sm text-ink'

  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const canPrev = page > 0
  const canNext = page < totalPages - 1
  const rangeStart = total === 0 ? 0 : page * pageSize + 1
  const rangeEnd = Math.min((page + 1) * pageSize, total)

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle>{t('eventsTable.title')}</CardTitle>
        <Select
          value={eventFilter ?? ALL_EVENTS_VALUE}
          onValueChange={(v) =>
            onEventFilterChange(
              v === ALL_EVENTS_VALUE ? null : (v as AnalyticsEventType)
            )
          }
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t('eventsTable.filterAll')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_EVENTS_VALUE}>
              {t('eventsTable.filterAll')}
            </SelectItem>
            {ANALYTICS_EVENT_TYPES.map((eventType) => (
              <SelectItem key={eventType} value={eventType}>
                {t(`breakdownValues.${eventType}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="m-4 h-32 animate-pulse rounded-xl bg-ink/[0.04]" />
        ) : !data?.items?.length ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            {t('eventsTable.empty')}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="border-b border-ink/[0.06]">
                  <tr>
                    <th className={TH}>{t('eventsTable.columns.time')}</th>
                    <th className={TH}>{t('eventsTable.columns.event')}</th>
                    <th className={TH}>{t('eventsTable.columns.apartment')}</th>
                    <th className={TH}>{t('eventsTable.columns.user')}</th>
                    <th className={TH}>{t('eventsTable.columns.listingType')}</th>
                    <th className={TH}>{t('eventsTable.columns.source')}</th>
                    <th className={TH}>{t('eventsTable.columns.session')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => {
                    const listingType = String(
                      row.properties.listing_type ?? 'unknown'
                    )
                    const source = row.properties.source
                      ? String(row.properties.source)
                      : '—'
                    const userId = row.properties.user_id
                      ? String(row.properties.user_id)
                      : null
                    const rawAptId = row.properties.apartment_id
                    const apartmentId =
                      typeof rawAptId === 'number'
                        ? rawAptId
                        : typeof rawAptId === 'string'
                          ? Number.parseInt(rawAptId, 10)
                          : NaN
                    const isAuthenticated =
                      row.properties.is_authenticated === true

                    return (
                      <tr
                        key={row.id}
                        className="border-b border-ink/[0.04] last:border-0"
                      >
                        <td className={TD}>
                          {format(new Date(row.created_at), 'd.M.yyyy HH:mm')}
                        </td>
                        <td className={TD}>
                          {t(`breakdownValues.${row.event_name}`, {
                            defaultValue: row.event_name
                          })}
                        </td>
                        <td className={TD}>
                          {!Number.isNaN(apartmentId) ? (
                            <Link
                              to={`/admin/apartments/${apartmentId}`}
                              className="font-mono text-xs text-primary hover:underline"
                            >
                              #{apartmentId}
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className={TD}>
                          {userId && isAuthenticated ? (
                            <Link
                              to={`/admin/users/${userId}`}
                              className="font-mono text-xs text-primary hover:underline"
                            >
                              {userId.slice(0, 8)}…
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">
                              {t('eventsTable.anonymous')}
                            </span>
                          )}
                        </td>
                        <td className={TD}>
                          {t(`breakdownValues.${listingType}`, {
                            defaultValue: listingType
                          })}
                        </td>
                        <td className={TD}>
                          {source === '—'
                            ? source
                            : t(`breakdownValues.${source}`, {
                                defaultValue: source
                              })}
                        </td>
                        <td
                          className={`${TD} font-mono text-xs text-muted-foreground`}
                        >
                          {row.session_id.slice(0, 12)}…
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink/[0.06] px-4 py-3">
              <p className="text-sm text-muted-foreground">
                {t('eventsTable.pagination', {
                  from: rangeStart,
                  to: rangeEnd,
                  total
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canPrev}
                  onClick={() => onPageChange(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('eventsTable.prev')}
                </Button>
                <span className="text-sm text-muted-foreground">
                  {t('eventsTable.pageOf', { page: page + 1, total: totalPages })}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canNext}
                  onClick={() => onPageChange(page + 1)}
                >
                  {t('eventsTable.next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
